import React, { useContext, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {
    ATTRIBUTION_MODE_CHERRYPICK,
    USER_RELATIONSHIP_ATTRIBUTION_ON,
} from '../../../contexts/AbstractContext';
import Layout from '../Layout';
import {
    FETCH_MODE_FEED,
    Provider as CommunityContextProvider,
} from '../../../contexts/CommunityContext';
import { PageContext } from '../../../contexts/ContextCreator';

import '../ThreeColumnLayout/ThreeColumnLayout.scss';
import './ThreeColumnGridLayout.scss';
import useStickyContent from '../../../hooks/useStickyContent';

const MobileWandTrigger = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../CallToAction/MobileWandTrigger/MobileWandTrigger'
    )
);
MobileWandTrigger.displayName = 'MobileWandTrigger';

const CreateContentWidgetV2 = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../Content/CreateContentWidgetV2/CreateContentWidgetV2'
    )
);
CreateContentWidgetV2.displayName = 'CreateContentWidgetV2';

const MasterFeedTitleCard = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../Content/Feed/Components/MasterFeedTitleCard/MasterFeedTitleCard'
    )
);
MasterFeedTitleCard.displayName = 'MasterFeedTitleCard';

const Feed = loadable(() =>
    import(/* webpackPrefetch: true */ '../../Content/Feed/Feed')
);
Feed.displayName = 'Feed';

const LeftRailContent = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../LeftRailLayout/LeftRailContent/LeftRailContent'
    )
);
LeftRailContent.displayName = 'LeftRailContent';

const RightRailContent = loadable(() =>
    import(/* webpackPrefetch: true */ './RightRailContent/RightRailContent')
);
RightRailContent.displayName = 'RightRailContent';

const TitleCard = loadable(() =>
    import(/* webpackPrefetch: true */ './TitleCard/TitleCard')
);
TitleCard.displayName = 'TitleCard';

const ProfileCTA = loadable(() =>
    import(
        /* webpackPreload: true */ '../../CallToAction/OnSiteProfileCTAs/JobLookingCTA/JobLookingCTA'
    )
);
const TopBannerProfileCTA = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../CallToAction/OnSiteProfileCTAs/TopBannerProfileCta/TopBannerProfileCta'
    )
);
TopBannerProfileCTA.displayName = 'TopBannerProfileCTA';

const PostBoxContainer = loadable(() =>
    import(
        /* webpackPrefetch: true */ '../../UnifiedPostBox/PostBoxContainer/PostBoxContainer'
    )
);

const ThreeColumnGridLayout = ({
    allowProfileCTA,
    children,
    currentTopic,
    displayDesktopFooter,
    hasBottomBanner,
    hasTitleCard,
    leftRailContent,
    profileLeftRailCTAProps,
    rightRailContent,
    showCreateContentWidget,
    showMobileWand,
    showSingleContentFeed,
    topics,
}) => {
    const {
        pageProps: {
            headerProps: { jobProfileSnippet: { isCompleted } = {} } = {},
            featureFlags = {},
        } = {},
        modals,
        isMobile,
        wrapAuth,
        getPageProps,
    } = useContext(PageContext);

    const isProfileCTARendered = useMemo(() => {
        if (!isMobile) return false;
        if (!featureFlags.onSiteCTA || !allowProfileCTA) return false;

        return !isCompleted;
    }, [isMobile, featureFlags.onSiteCTA, allowProfileCTA, isCompleted]);

    const isMobileWandShown = useMemo(() => {
        return showMobileWand && !modals.type;
    }, [showMobileWand, modals?.type]);

    const contentWidget = useCallback(() => {
        if (!showCreateContentWidget || currentTopic) return null;
        if (featureFlags.unifiedPostBox) return <PostBoxContainer />;

        return <CreateContentWidgetV2 defaultPostLocation={currentTopic} />;
    }, [showCreateContentWidget, featureFlags.unifiedPostBox, currentTopic]);

    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const [leftStyles] = useStickyContent(leftRef, false, 40);
    const [rightStyles] = useStickyContent(rightRef, false, 30);

    return (
        <div className="component-Layout-ThreeColumnGridLayout">
            <Layout
                PAGE_PROPS={getPageProps()}
                hasBottomBanner={hasBottomBanner}
                hasChangingContainer={false}
                fullScreenStyling
                displayDesktopFooter={displayDesktopFooter}
            >
                {isProfileCTARendered ? (
                    <TopBannerProfileCTA
                        LAT="community-feed-profile-cta-click"
                        LAC="community-feed-profile-cta-click"
                        clickMetric="community-feed-profile-cta-click"
                        impressionMetric="community-feed-profile-cta-click"
                    />
                ) : null}
                <div className="component-Layout-ThreeColumnLayout">
                    <div
                        className="ThreeRailLayout-LeftRail"
                        ref={leftRef}
                        style={leftStyles}
                    >
                        {leftRailContent || (
                            <LeftRailContent
                                allowProfileCTA={allowProfileCTA}
                                displayLeftRailFooter={!displayDesktopFooter}
                                profileLeftRailCTAProps={
                                    profileLeftRailCTAProps
                                }
                            />
                        )}
                    </div>
                    <div className="ThreeRailLayout-MainContent">
                        {hasTitleCard && currentTopic ? (
                            <TitleCard currentTopic={currentTopic} />
                        ) : null}
                        {contentWidget()}
                        {children}
                        {showSingleContentFeed ? (
                            <>
                                <ProfileCTA
                                    LAC="under-comments"
                                    LAT="under-comments"
                                    clickMetric="under-comments-profile-cta-click"
                                    impressionMetric="under-comments-profile-cta-impression"
                                />
                                <MasterFeedTitleCard />
                                {featureFlags.unifiedPostBox ? (
                                    wrapAuth(
                                        'CreateContent',
                                        <PostBoxContainer />,
                                        {
                                            login_trigger: 'create-content',
                                        }
                                    )
                                ) : (
                                    <CreateContentWidgetV2
                                        defaultPostLocation={currentTopic}
                                    />
                                )}
                                <CommunityContextProvider
                                    fetchMode={FETCH_MODE_FEED}
                                    attributionMode={
                                        ATTRIBUTION_MODE_CHERRYPICK
                                    }
                                    content={[]}
                                    userRelationshipMode={
                                        USER_RELATIONSHIP_ATTRIBUTION_ON
                                    }
                                    topics={topics}
                                >
                                    <Feed
                                        feedGroup="GlobalFeed"
                                        feedGroupId="master"
                                        feedType="masterFeed"
                                    />
                                </CommunityContextProvider>
                            </>
                        ) : null}
                        {isMobileWandShown ? (
                            <MobileWandTrigger currentTopic={currentTopic} />
                        ) : null}
                    </div>
                    <div
                        className="ThreeRailLayout-RailRail"
                        ref={rightRef}
                        style={rightStyles}
                    >
                        <RightRailContent contents={rightRailContent} />
                    </div>
                </div>
            </Layout>
        </div>
    );
};

export default ThreeColumnGridLayout;

// TODO: define prop-type shapes for .object props below
ThreeColumnGridLayout.propTypes = {
    profileLeftRailCTAProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    /**
     * leftRailContent
     */
    leftRailContent: PropTypes.node,
    /**
     * main content
     */
    children: PropTypes.node.isRequired,
    /**
     * right rail content
     */
    rightRailContent: PropTypes.arrayOf(PropTypes.string),
    /**
     * Required if need mini feed
     */
    currentTopic: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    /**
     * bool to show create content widget or not
     */
    showCreateContentWidget: PropTypes.bool,
    /**
     * bool to show bottom banner or not
     */
    hasBottomBanner: PropTypes.bool,
    /**
     * bool to show mobile wand toggle or not
     */
    showMobileWand: PropTypes.bool,
    showSingleContentFeed: PropTypes.bool,
    hasTitleCard: PropTypes.bool,
    displayDesktopFooter: PropTypes.bool,
    /**
     * whether Profile CTA option is on if other checks pass.
     */
    allowProfileCTA: PropTypes.bool,
    topics: PropTypes.arrayOf(PropTypes.string),
};

ThreeColumnGridLayout.defaultProps = {
    displayDesktopFooter: false,
    hasTitleCard: false,
    showSingleContentFeed: false,
    showMobileWand: false,
    showCreateContentWidget: true,
    hasBottomBanner: false,
    allowProfileCTA: false,
    profileLeftRailCTAProps: {},
    leftRailContent: null,
    rightRailContent: [],
    currentTopic: {},
    topics: [],
};
